import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatSpinner } from '@angular/material/progress-spinner';
import { FieldTypeConfig } from '@ngx-formly/core';
import { FieldType } from '@ngx-formly/material/form-field';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { FilterPipe } from '../pipes/filter.pipe';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';

interface ApplicationLookupCard {
  id: string;
  disabled?: boolean;
  disabledReason?: string;
  [key: string]: any;
}

interface ApplicationLookupResult {
  totalApplications: number;
  applications: ApplicationLookupCard[];
  message: string;
}

@Component({
  selector: 'itc-app-card-list',
  styleUrls: ['./app-card-list.component.scss'],
  template: `
    @if (lookupResult$ | async; as lookupResult) {
      <div>
        @if (lookupResult.message) {
          <div>
            <div class="alert alert-info">
              {{ lookupResult.message }}
            </div>
          </div>
        }
        @if (lookupResult.applications?.length > 0) {
          <div>
            <div>
              {{ lookupResult.totalApplications }} results found. Please select from the list below
            </div>
            @for (card of lookupResult.applications; track card; let i = $index) {
              <div
                (click)="selectCard(card, i)"
                [class.card-list__item--disabled]="isCardDisabled(card)"
                [attr.aria-disabled]="isCardDisabled(card)"
                >
                <mat-card [ngClass]="activeState[i] ? 'active-card' : ''">
                  <mat-card-content>
                    <div class="card-list__application-info">
                      <div
                        class="inner-card-list"
                        [ngClass]="activeState[i] ? 'selected-card' : ''"
                        >
                        <div class="summary">
                          <mat-icon
                            class="card-list__icon"
                      svgIcon="{{
                        card.system?.energySourceId ?? card.energySourceId
                      }}"
                          ></mat-icon>
                          <div class="card-list__details">
                            <div class="card-list__level">
                              {{ card.typeName }}
                              <span class="status">{{ card.statusName }}</span>
                            </div>
                            <div class="card-list__address_capacity">
                              <div>
                                {{ card.system?.fullAddress ?? card.systemAddress }}
                                @if (getCardDisabledReason(card)) {
                                  <div class="card-list__disabled-reason">
                                    {{ getCardDisabledReason(card) }}
                                  </div>
                                }
                              </div>
                              <div class="card-list__address_capacity__capacity">
                                {{ card.applicationCapacity }} kW
                              </div>
                            </div>
                            @if (
                              card.applicationUDFDetails &&
                              card.applicationUDFDetails.length > 0
                              ) {
                              <div
                                class="card-list__id"
                                >
                                @for (
                                  udf of card.applicationUDFDetails
                                  | filter: 'id':['QueueNumber', 'ProjectID']; track
                                  udf; let indx = $index) {
                                  <span
                                    >
                                    @switch (udf.type) {
                                      @case ('Date') {
                                        <span>{{
                                          udf.value | date: 'MM/dd/yyyy'
                                        }}</span>
                                      }
                                      @default {
                                        <span>{{ udf.value }} </span>
                                      }
                                    }
                                  </span>
                                }
                              </div>
                            }
                            <div class="card-list__name">
                              {{ card.customerFullName ?? card.projectName }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </mat-card-content>
                </mat-card>
                <br />
              </div>
            }
          </div>
        } @else {
          <div>
            {{ noResultsText }}
          </div>
        }
      </div>
    } @else {
      @if (errorObject) {
        <div class="col-12 text-center">
          <div class="alert alert-danger">
            {{ errorObject.message }}
          </div>
        </div>
      } @else {
        @if (props.isLoading) {
          <div class="in-place-spinner-wrapper">
            <mat-spinner></mat-spinner>
          </div>
        }
      }
    }
    `,
  imports: [
    MatSpinner,
    DatePipe,
    CommonModule,
    FilterPipe,
    MatIconModule,
    MatIconModule,
    MatCardModule,
  ],
})
export class AppCardListComponent
  extends FieldType<FieldTypeConfig>
  implements OnInit
{
  constructor() {
    super();
  }

  errorObject = null;
  control: UntypedFormControl;
  lookupResult$: Observable<ApplicationLookupResult>;

  noResultsText: string;

  activeState: boolean[] = [];

  ngOnInit() {
    this.control = this?.formControl as UntypedFormControl;
    this.noResultsText = this.props.noResultsText;
    this.lookupResult$ = this.props.lookupResult$;
    this.lookupResult$ = this.lookupResult$.pipe(
      tap(() => {
        this.control.setValue(null);
        this.activeState = [];
        this.props.isLoading = false;
      }),
      catchError((err) => {
        this.props.isLoading = false;
        this.errorObject = err;
        return throwError(err);
      })
    );
  }

  isCardDisabled(card: ApplicationLookupCard): boolean {
    return !!card.disabled;
  }

  getCardDisabledReason(card: ApplicationLookupCard): string {
    return card.disabledReason || '';
  }

  selectCard(card: ApplicationLookupCard, index: number) {
    if (this.isCardDisabled(card)) {
      return;
    }

    this.setFormValue(card, index);
    this.props.onClick?.(card);
  }

  setFormValue(app: ApplicationLookupCard, index: number) {
    this.control.setValue(app.id);
    this.activeState.fill(false);
    this.activeState[index] = true;
  }

  //   onClick($event) {
  //     if (this.props.onClick) {
  //       this.props.onClick($event);
  //     }
  //   }
}
